import { IconButton } from '@neo4j-ndl/react';
import { PlayCircleIconOutline, StopCircleIconOutline } from '@neo4j-ndl/react/icons';
import { useFeatureFlag } from '@nx/state';
import type { DbmsInfo } from '@nx/state/desktop';
import { DBMS_STATUS } from '@nx/state/desktop';

import { InstanceCardHeader } from '../../components/instance-card-wrappers';
import {
  CompactConnectButton,
  ConnectDropdownButton,
  SplitConnectButton,
} from '../../components/instance-connect-buttons';
import { useDbmsConnection } from '../hooks/use-dbms-connection';
import { useToggleInstanceRunStatus } from '../hooks/use-toggle-instance-run-status';
import type { InstanceDetailsTab } from '../instance-details';
import { MoreMenu } from './more-menu';

export const InstanceCardHeaderWithControls = ({
  instance,
  onConnectionSuccess,
  onInstanceDetailsClick,
  isCompact = false,
}: {
  instance: DbmsInfo;
  onConnectionSuccess?: () => void;
  onInstanceDetailsClick: (initialTab?: InstanceDetailsTab) => void;
  /** Minimize the interactions available in the Instance card */
  isCompact?: boolean;
}) => {
  const [shouldOneClickSwitch] = useFeatureFlag('local:one-click-instance-switch');
  const { toggleRunStatus, isStopped, isStarted, isStarting, isStopping } = useToggleInstanceRunStatus(instance);
  const dbmsConnection = useDbmsConnection(
    {
      id: instance.id,
      name: instance.name,
      connectionUri: instance.connectionUri,
      dbmsIsRunning: instance.status === DBMS_STATUS.STARTED,
      credentialsType: 'dbms',
    },
    onConnectionSuccess,
  );

  // The instance is in a state where we can either connect or disconnect to it.
  const canConnectOrDisconnect = isStarted && !isStarting && !isStopping && !dbmsConnection.isLoading;

  if (isCompact && shouldOneClickSwitch) {
    return (
      <InstanceCardHeader instanceName={instance.name}>
        <CompactConnectButton
          instanceInfo={instance}
          canConnectOrDisconnect={canConnectOrDisconnect}
          dbmsConnection={dbmsConnection}
        />
      </InstanceCardHeader>
    );
  }

  return (
    <InstanceCardHeader instanceName={instance.name}>
      <div className="flex gap-2">
        {isStopped ? (
          <IconButton
            onClick={() => {
              void toggleRunStatus();
            }}
            isDisabled={isStarting}
            ariaLabel="Start instance"
            size="medium"
          >
            <PlayCircleIconOutline />
          </IconButton>
        ) : (
          <IconButton
            onClick={() => {
              void toggleRunStatus();
            }}
            isDisabled={isStopping}
            ariaLabel="Stop instance"
            size="medium"
          >
            <StopCircleIconOutline />
          </IconButton>
        )}
        {isCompact ? (
          <SplitConnectButton
            instanceInfo={instance}
            canConnectOrDisconnect={canConnectOrDisconnect}
            dbmsConnection={dbmsConnection}
          />
        ) : (
          <ConnectDropdownButton canConnectOrDisconnect={canConnectOrDisconnect} dbmsConnection={dbmsConnection} />
        )}
        {!isCompact && <MoreMenu instance={instance} onInstanceDetailsClick={onInstanceDetailsClick} />}
      </div>
    </InstanceCardHeader>
  );
};
